import { connect } from 'pwa-helpers';
import { html, css, nothing } from "lit";
import { customElement, property, state } from 'lit/decorators.js';
import store, { waitForUser } from "../../store/store";
import { StoreState } from "../../store/store.interface";
import variables from '../../common/styles/variables';
import { BaseComponent } from '../../features/base-component/base-component';
import { booleanConverter } from '../../common/converters';
import { ShopCart } from '../cart/shop-cart/interfaces/shop-cart.interface';
import { UserProfile } from '../user-profile/user-profile.interface';
import { getCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { selectUser } from '../user-profile/user-profile.selectors';
import { UserStatus } from '../user-profile/user-profile.interface';
import { getShopCart } from '../cart/shop-cart/thunks/get-shop-cart.thunk';
import { getAccessCodeData } from '../access-code/store/access-code.selector';
import { ActionStatus } from '../../common/constants/action-status';

/**
 * The possible context states that can be passed in to the display property
 */
export declare type ContextState = 'empty-cart' | 'item-in-cart' | 'false' | 'instructor' | 'student' | 'rapid-order' | 'verified-instructor' | 'verified-he-instructor' | 'access-code-invalid' | 'access-code-used' | 'access-code-expired';

const contextConverter = {
  fromAttribute: (value: string): Array<Array<ContextState>> | Array<ContextState> | ContextState => {
    const removedWhitespaceValue = value.replace(/ /g, '');
    if (!removedWhitespaceValue.startsWith('[')) {
      return value as ContextState;
    }

    try {
      const parsed = JSON.parse(value);
      return isArrayOfArrays(parsed) || isArrayOfStrings(parsed) ? parsed : 'false';
    } catch {
      return 'false';
    }
  },
  toAttribute: (value: Array<Array<ContextState>> | Array<ContextState> | ContextState): string => {
    return JSON.stringify(value);
  }
}

/**
 * An element to display based on user context
 *
 * @slot default slot - The content to display when the provided condition is true.
 * @slot main - The content to display when the provided condition is true.
 * @slot alt - This slot is optional and will only be displayed when the provided condition is false.
 */
@customElement('mhecomm-context')
export class Context extends connect(store)(BaseComponent) {
  static styles = [variables, css`
    :host {
      display: block;
    }
  `];

  /**
   * Under what conditions the content should be displayed. The slot with the name "alt" will be shown if the conditions evaluate to false.
   * This should either be a single string, an array of strings, or an array of array of strings.
   *
   * If it is a single string, the content is displayed if the current state matches the provided state.
   * If an array of strings, the content is displayed if the current state matches all the provided states.
   * If an array of array of strings, the content is displayed with the items in the inner array being combined with 'OR' logic and the items in the outer array being combiend with 'AND' logic.
   *
   * All strings should be one of the following possible states:
   *   empty-cart, item-in-cart, instructor, student
   */
  @property({ converter: contextConverter })
  display: Array<Array<ContextState>> | Array<ContextState> | ContextState | undefined = undefined;

  @property({ converter: booleanConverter })
  forceDisplay = false;

  @property({ converter: booleanConverter })
  inverseLogic = false;

  @property({ converter: booleanConverter })
  waitForState = false;

  @property()
  store?: string

  @state()
  private cart?: ShopCart;

  @state()
  private user?: UserProfile;

  @state()
  private accessCodeStatus?: string;

  @state()
  private showing = false;

  @state()
  private segSegmentValues = ["seg", "k12ecommerce"];

  @state()
  private loading = true;

  connectedCallback() {
    super.connectedCallback();
    this.removeMainSlotHiddenClass();
    store.dispatch(getShopCart({ store: this.store }));
  }

  renderComp() {
    if (this.loading) {
      return nothing;
    }

    return html`
      ${(this.showing || this.forceDisplay) ? html`
        <slot name="main"></slot>
      `: ''}
      ${(!this.showing || this.forceDisplay) ? html`
        <slot name="alt"></slot>
      ` : ''}
    `;
  }

  update(changedProperties: any) {
    super.update(changedProperties);
    this.removeMainSlotHiddenClass();
    this.showing = this.determineShowing();
  }

  async stateChanged(state: StoreState) {
    this.loading = true;

    if (!window.Mhecomm_Editor && this.waitForState) {
      await waitForUser();
    }

    this.cart = getCart(state);
    this.user = selectUser(state);
    const stateStatus = state.accessCodeState.status as string;
    this.accessCodeStatus = getAccessCodeData(state)?.status ||
      (['INVALID', 'USED', 'EXPIRED'].includes(stateStatus) ? stateStatus : undefined) ||
      (state.accessCodeState.status === ActionStatus.failed ? 'INVALID' : undefined);
    this.showing = this.determineShowing();

    this.loading = false;
  }

  private removeMainSlotHiddenClass() {
    [...this.children].forEach((el) => el.classList.remove("d-none"));
  }

  determineShowing() {
    if (typeof this.display === 'string') {
      return this.isTrue(this.display);
    }

    if (typeof this.display === 'undefined') {
      return true;
    }

    if (isArrayOfStrings(this.display)) {
      return this.showingArrayOfStrings();
    }

    if (isArrayOfArrays(this.display)) {
      return this.showingArrayOfArrays();
    }

    return false;
  }

  isTrue(contextState: ContextState) {
    const functionalMapping: { [key: string]: () => boolean } = {
      'instructor': this.isInstructor,
      'student': this.isStudent,
      'empty-cart': this.isEmptyCart,
      'item-in-cart': this.hasItemInCart,
      'logged-in': this.isLoggedIn,
      'logged-out': this.isLoggedOut,
      'higher-ed-user': this.isHigherEdUser,
      'k12-user': this.isK12User,
      'rapid-order': this.isRapidOrder,
      'he-instructor': this.isHEInstructor,
      'seg-instructor': this.isSEGInstructor,
      'verified-instructor': this.isVerifiedInstructor,
      'verified-he-instructor': this.isVerifiedHEInstructor,
      'access-code-invalid': this.isAccessCodeInvalid,
      'access-code-used': this.isAccessCodeUsed,
      'access-code-expired': this.isAccessCodeExpired
    };

    const stateFound = Object.keys(functionalMapping).includes(contextState);
    const wasTrue = stateFound ? functionalMapping[contextState].bind(this)() : false;
    const inverseApplied = this.inverseLogic ? !wasTrue : wasTrue;
    return stateFound ? inverseApplied : false;
  }

  isInstructor() {
    return this.user?.userType === 'instructor';
  }

  isVerifiedInstructor() {
    return this.user?.userType === 'instructor' && this.user?.status === UserStatus.active;
  }

  isHEInstructor() {
    return this.isInstructor() && this.isHigherEdUser();
  }

  isVerifiedHEInstructor() {
    return this.isVerifiedInstructor() && this.isHigherEdUser();
  }

  isSEGInstructor() {
    return this.isInstructor() && this.isSegEdUser();
  }

  isStudent() {
    return this.user?.userType === 'learner';
  }

  isEmptyCart() {
    return this.cart === undefined || this.cart.productCount === 0;
  }

  hasItemInCart() {
    return this.cart !== undefined && this.cart.productCount >= 1;
  }

  isLoggedIn() {
    return !!this.user?.givenName;
  }

  isLoggedOut() {
    return !this.user?.givenName;
  }

  isHigherEdUser() {
    return this.user?.persona?.segment?.toLowerCase() === "he";
  }

  isSegEdUser() {
    return this.user?.persona?.segment?.toLowerCase() === "seg";
  }

  isK12User() {
    if (!this.user) {
      return false;
    }

    const segment = this.user.persona?.segment?.toLowerCase() ?? '';
    return this.segSegmentValues.includes(segment);
  }

  isRapidOrder() {
    return new URLSearchParams(window.location.search).get('rapidOrder') === 'true';
  }

  private isAccessCodeInvalid() {
    return this.accessCodeStatus === 'INVALID';
  }

  private isAccessCodeUsed() {
    return this.accessCodeStatus === 'USED';
  }

  private isAccessCodeExpired() {
    return this.accessCodeStatus === 'EXPIRED';
  }

  private showingArrayOfStrings() {
    const displayConfig = this.display as Array<ContextState>;
    return displayConfig.every((contextState) => this.isTrue(contextState));
  }

  private showingArrayOfArrays() {
    const displayConfig = this.display as Array<Array<ContextState>>;
    return displayConfig.some((arr) => arr.every((contextState) => this.isTrue(contextState)));
  }
}

function isArrayOfStrings(display: Array<Array<ContextState>> | Array<ContextState> | ContextState | undefined) {
  return Array.isArray(display) && display.length > 0 && typeof display[0] === 'string';
}

function isArrayOfArrays(display: Array<Array<ContextState>> | Array<ContextState> | ContextState | undefined) {
  return Array.isArray(display) && display.length > 0 && Array.isArray(display[0]);
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-context': Context;
  }
}
